/**
 * 从公式生成两份混合着色器（M1-11 / DESIGN.md §2.8、§4.4）。
 *
 * 输入是 `modes.ts` 里那**一份**公式，输出是 WGSL 与 GLSL 两份源码。
 * `ShaderSource` 要求两份同时提供，这里一次把两份都产出来——
 * 让"两份不同步"在结构上无法发生，而不是靠纪律保证。
 *
 * 色彩空间：§4.4 要求「混合在线性空间还是 sRGB 空间由文档位深与用户设置决定，
 * 作为着色器 uniform 传入，不要硬编码」。这里 `linear` 是 uniform，
 * 非零时先把两个输入转成线性、混合完再转回来。
 */
import type { ShaderSource } from '../../backend';
import {
  GLSL_DIALECT,
  WGSL_DIALECT,
  formatFloat,
  type ShaderDialect,
  type ShaderParam,
} from './dialect';
import {
  BLEND_FORMULAS,
  SEPARABLE_BLEND_MODES,
  blendFunctionName,
  type SeparableBlendMode,
} from './modes';
import {
  NONSEPARABLE_BLEND_MODES,
  NONSEPARABLE_FORMULAS,
  nonSeparableFunctionName,
  nonSeparableHelpers,
  type NonSeparableBlendMode,
} from './nonseparable';

/**
 * 全部十六种混合模式。
 *
 * 可分离的十二种在前、非可分离的四种在后——序号一旦排定就是着色器与 CPU 之间
 * 的约定，中间插入新模式会让所有已保存文档的混合模式整体错位。
 * 新模式只能追加到末尾。
 */
export const ALL_BLEND_MODES = [
  ...SEPARABLE_BLEND_MODES,
  ...NONSEPARABLE_BLEND_MODES,
] as const;

export type AnyBlendMode = SeparableBlendMode | NonSeparableBlendMode;

/** uniform 布局。两种方言共用这一份声明，字段名因此不会走样。 */
const UNIFORMS: readonly ShaderParam[] = [
  // vec4 而非 vec3，且顺序无关紧要（uniformLayout 会排序）——两条约束的原因
  // 见 dialect.ts 的 uniformLayout：后端紧密打包，vec3 的 16 字节对齐会错位
  { name: 'cb', type: 'vec4' },
  { name: 'cs', type: 'vec4' },
  /** 模式序号，见 {@link blendModeIndex}。用 float 是因为两个后端都保证支持。 */
  { name: 'mode', type: 'float' },
  /** 非零表示在线性空间混合。 */
  { name: 'linear', type: 'float' },
];

/** 模式在着色器里的序号。CPU 侧按同一张表传 uniform。 */
export function blendModeIndex(mode: AnyBlendMode): number {
  const index = (ALL_BLEND_MODES as readonly string[]).indexOf(mode);
  if (index < 0) throw new RangeError(`未知的混合模式：${mode}`);
  return index;
}

/** 该模式是否需要跨通道运算。 */
export function isNonSeparable(mode: AnyBlendMode): mode is NonSeparableBlendMode {
  return (NONSEPARABLE_BLEND_MODES as readonly string[]).includes(mode);
}

/**
 * sRGB ↔ 线性的转换函数。
 *
 * 用 CSS/IEC 61966-2-1 的分段定义，不是 `pow(c, 2.2)` 的近似——
 * 近似在暗部差得最多，而暗部正是多层混合后最容易看出断层的地方。
 */
export function colorSpaceFunctions(d: ShaderDialect): string {
  const cutoffLinear = d.splat(0.04045);
  const cutoffSrgb = d.splat(0.0031308);
  const srgbToLinear = d.functionDef(
    'srgb_to_linear',
    [{ name: 'c', type: 'vec3' }],
    'vec3',
    `return ${d.select(
      `pow((c + ${d.splat(0.055)}) / ${d.splat(1.055)}, ${d.splat(2.4)})`,
      `c / ${d.splat(12.92)}`,
      d.lessEqual('c', cutoffLinear),
    )};`,
  );
  const linearToSrgb = d.functionDef(
    'linear_to_srgb',
    [{ name: 'c', type: 'vec3' }],
    'vec3',
    `return ${d.select(
      `${d.splat(1.055)} * pow(c, ${d.splat(1 / 2.4)}) - ${d.splat(0.055)}`,
      `c * ${d.splat(12.92)}`,
      d.lessEqual('c', cutoffSrgb),
    )};`,
  );
  return `${srgbToLinear}\n\n${linearToSrgb}`;
}

/** 十六个混合函数。每个由公式渲染，公式本身只有一份。 */
export function blendFunctions(d: ShaderDialect): string {
  const params = [
    { name: 'cb', type: 'vec3' },
    { name: 'cs', type: 'vec3' },
  ] as const;

  const separable = SEPARABLE_BLEND_MODES.map((mode) =>
    d.functionDef(blendFunctionName(mode), params, 'vec3', BLEND_FORMULAS[mode](d)),
  );
  const nonSeparable = NONSEPARABLE_BLEND_MODES.map((mode) =>
    d.functionDef(nonSeparableFunctionName(mode), params, 'vec3', NONSEPARABLE_FORMULAS[mode](d)),
  );
  return [...separable, ...nonSeparable].join('\n\n');
}

/** 混合函数的名字，两类模式共用一个入口。 */
function functionNameFor(mode: AnyBlendMode): string {
  return isNonSeparable(mode) ? nonSeparableFunctionName(mode) : blendFunctionName(mode);
}

/**
 * 按序号分发到具体的混合函数。
 *
 * 用 if 链而不是查表：两种着色器语言都不支持函数指针数组，
 * 而 if 链在两边的语法恰好一致，只有函数签名需要方言处理。
 */
export function dispatchFunction(d: ShaderDialect): string {
  const branches = ALL_BLEND_MODES.map((mode, index) => {
    const name = functionNameFor(mode);
    return `if (mode < ${formatFloat(index + 0.5)}) { return ${name}(cb, cs); }`;
  }).join('\n');

  return d.functionDef(
    'blend_dispatch',
    [
      { name: 'mode', type: 'float' },
      { name: 'cb', type: 'vec3' },
      { name: 'cs', type: 'vec3' },
    ],
    'vec3',
    // 兜底返回 cs（normal）：序号越界时给出一个确定的结果，
    // 而不是让着色器编译器抱怨"并非所有路径都有返回值"
    `${branches}\nreturn cs;`,
  );
}

/** 片元入口：按 uniform 决定色彩空间，混合，写出。 */
function fragmentBody(d: ShaderDialect): string {
  const u = (name: string): string => d.uniform(name);
  const inLinear = `(${u('linear')} > ${formatFloat(0.5)})`;
  return [
    d.local('cb_raw', `${u('cb')}.rgb`),
    d.local('cs_raw', `${u('cs')}.rgb`),
    // 转换整体套在 select 里，两条路径都算了再选一条：着色器里分支的两边
    // 本来就都会被执行，写成 if 也不会更快，但会多一份代码
    d.local(
      'cb_work',
      d.select('cb_raw', 'srgb_to_linear(cb_raw)', vecCond(d, inLinear)),
    ),
    d.local(
      'cs_work',
      d.select('cs_raw', 'srgb_to_linear(cs_raw)', vecCond(d, inLinear)),
    ),
    d.local('blended', `blend_dispatch(${u('mode')}, cb_work, cs_work)`),
    d.local(
      'result',
      d.select('blended', 'linear_to_srgb(blended)', vecCond(d, inLinear)),
    ),
    d.name === 'wgsl' ? 'return vec4f(result, 1.0);' : 'fragColor = vec4(result, 1.0);',
  ].join('\n');
}

/**
 * 把标量条件铺成向量条件。
 *
 * 逐分量的 select 要求选择子与被选值同宽：WGSL 的 `select` 与 GLSL 的 `mix(…, bvec)`
 * 都不接受一个标量 bool 去选 vec3。
 */
export function vecCond(d: ShaderDialect, scalarCond: string): string {
  return d.name === 'wgsl' ? `vec3<bool>(${scalarCond})` : `bvec3(${scalarCond})`;
}

function renderWgsl(): string {
  const d = WGSL_DIALECT;
  return [
    '// 由 src/gpu/shaders/blend/generate.ts 生成，请勿手改。',
    d.uniformBlock(UNIFORMS),
    d.fullscreenPreamble(),
    colorSpaceFunctions(d),
    nonSeparableHelpers(d),
    blendFunctions(d),
    dispatchFunction(d),
    d.fragmentMain(fragmentBody(d)),
  ].join('\n\n');
}

function renderGlslFragment(): string {
  const d = GLSL_DIALECT;
  return [
    '#version 300 es',
    '// 由 src/gpu/shaders/blend/generate.ts 生成，请勿手改。',
    'precision highp float;',
    d.uniformBlock(UNIFORMS),
    'out vec4 fragColor;',
    colorSpaceFunctions(d),
    nonSeparableHelpers(d),
    blendFunctions(d),
    dispatchFunction(d),
    d.fragmentMain(fragmentBody(d)),
  ].join('\n\n');
}

/**
 * 混合着色器源码（两份）。
 *
 * 生成一次即可，结果是纯函数产物，缓存起来避免每次建程序都重新拼字符串。
 */
let cached: ShaderSource | null = null;

export function separableBlendShader(): ShaderSource {
  cached ??= {
    wgsl: renderWgsl(),
    glsl: { vertex: GLSL_DIALECT.fullscreenPreamble(), fragment: renderGlslFragment() },
  };
  return cached;
}

/** 供测试用：绕过缓存重新生成。 */
export function regenerateSeparableBlendShader(): ShaderSource {
  cached = null;
  return separableBlendShader();
}
